import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import DataGrid from "../../components/common/dataGrid/dataGrid";
import DataGridHeader from "../../components/common/dataGrid/dataGridAddPatientHeader";
import { generateColumnMetadata, Feature } from "../../utils/utils";
import { commonPostApi } from "../../utils/apiService";
import { API_ENDPOINTS } from "../../utils/apiEndpoints";
import AddPayments from "./addPayment";
import AddInvoice from "./AddInvoice";
import { Button } from "primereact/button";
import _ from "lodash";
import { useSelector } from "react-redux";
import InvoicePrint from "../../components/features/printPage/InvoicePrint";
import { useReactToPrint } from "react-to-print";
import { exportCSV, exportExcel, exportPdf } from "../../utils/exportUtils";
import { SORT_ORDER } from "../../constants/sortOrder";

const Payment = forwardRef(
  (
    {
      header,
      rowExpansionTemplate,
      accordion,
      api,
      data,
      print,
      noRequired = ["payment_categories"],
      requiredButton,
      setInvoiceDetails,
      setPatientInvoiceDetails,
      ...props
    },
    ref,
  ) => {
    const printRef = useRef(null);
    const [columns, setColumns] = useState([]);
    const [values, setValues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedRows, setExpandedRows] = useState(null);
    const [addInvoiceVisisble, setAddInvoiceVisisble] = useState(false);
    const [addPaymentVisisble, setAddPaymentVisisble] = useState(false);
    const [metaData, setMetaData] = useState({});
    const id = useSelector((state) => state.common.camp?.camp_details_id);
    const tenantId = useSelector(
      (state) => state.common.organization?.tenant_id,
    );
    const [navigator, setNavigator] = useState({
      page: 1,
      first: 0,
      rows: 10,
      totalRecords: 1,
    });
    const [invoiceData, setInvoiceData] = useState({});

    const fetchData = (id) => {
      console.log("Fetching invoice data for ID:", id);
      commonPostApi(API_ENDPOINTS.PRINT_INVOICE, {
        collectable_payment_description_id: id,
        tenant_id: tenantId,
      })
        .then((data) => {
          console.log("Received invoice data:", data?.data?.data);
          if (data?.data?.data) {
            setInvoiceData((dat) => ({ ...dat, ...data?.data?.data[0] }));
          } else {
            console.error("No invoice data received");
          }
        })
        .catch((error) => {
          console.error("Error fetching invoice data:", error);
        });
    };

    useImperativeHandle(ref, () => ({
      callChild() {
        console.log("callChild");
      },
    }));

    const init = async (navigator) => {
      let currentObj = {
        query: {
          page: navigator?.page,
          limit: navigator?.rows,
          sortFields: navigator?.sortFields,
          searchCriteria: props?.patientId
            ? props?.patientId
            : navigator?.searchCriteria,
        },
      };

      if (!api) return setLoading(false);
      Promise.all([
        await commonPostApi(api, currentObj),
        props?.patientId &&
          (await commonPostApi(API_ENDPOINTS.GET_PATIENT_INVOICE_SUMMARY, {
            patient_id: props?.patientId,
          })),
      ])
        .then(async ([data = [], patientInvoice]) => {
          if (data?.data?.data) {
            let finalData = data?.data?.data;
            const updatedPatient = finalData;
            setInvoiceDetails &&
              setInvoiceDetails({
                client: data?.data?.clients,
                balance: data?.data?.total_balance_amount,
                paid: data?.data?.total_paid_amount,
                totalAmount: data?.data?.total_collectable_amount,
                total: data?.data?.total,
              });
            setPatientInvoiceDetails &&
              setPatientInvoiceDetails({
                balance: patientInvoice?.data?.data?.total_balance_amount,
                paid: patientInvoice?.data?.data?.total_paid_amount,
                totalAmount:
                  patientInvoice?.data?.data?.total_collectable_amount,
              });
            setNavigator((prev) => ({
              ...prev,
              totalRecords: data?.data?.total,
              page: data?.data?.currentPage,
            }));
            setColumns(
              generateColumnMetadata({
                data: finalData,
                nonRequireField: noRequired,
                alias: props?.headerAlias,
                defaultColumnsArr: props?.defaultColumns,
              }),
            );
            setValues(updatedPatient);
            setLoading(false);
          } else {
            setNavigator((prev) => ({
              ...prev,
              totalRecords: 1,
              page: 1,
            }));
          }
        })
        .catch((err) => {
          console.log(err);
        });
    };

    function handleClick(event) {
      setAddInvoiceVisisble(true);
    }

    function handleSeachChange(value) {
      props?.searchField &&
        setNavigator((prev) => ({
          ...prev,
          // searchCriteria: convertToSearchField(props?.searchField, value),
          searchCriteria: value,
        }));
      props?.searchField &&
        init({
          ...navigator,
          searchCriteria: value,
          // searchCriteria: convertToSearchField(props?.searchField, value),
        });
    }

    const onRowCountChange = (e) => {
      setLoading(true);
      setNavigator((prev) => ({
        ...prev,
        rows: e?.target?.value,
        page: 1,
        first: 0,
      }));
      init({
        ...navigator,
        page: 1,
        first: 0,
        rows: e?.target?.value,
      });
    };

    function onPageChange(event) {
      setLoading(true);
      setNavigator((prev) => ({
        ...prev,
        first: event?.first,
        page: event.page + 1,
      }));
      init({
        ...navigator,
        first: event?.first,
        page: event.page + 1,
      });
    }

    const onSortClick = (event) => {
      setLoading(true);
      const sortOrder =
        event?.sortOrder === 1 ? SORT_ORDER.ASC : SORT_ORDER.DESC;
      setNavigator((prev) => ({
        ...prev,
        sortBy: event?.sortField,
        sortDir: sortOrder,
        sortFields: [[event?.sortField, sortOrder]],
      }));
      init({
        ...navigator,
        sortBy: event?.sortField,
        sortDir: sortOrder,
        sortFields: [[event?.sortField, sortOrder]],
      });
    };

    const onRowToggle = (event) => {
      setExpandedRows(event.data);
    };

    useEffect(() => {
      if (props?.paymentData) {
        setColumns(
          generateColumnMetadata({
            data: props?.paymentData,
            alias: props?.headerAlias,
          }),
        );
        setValues(props?.paymentData);
      }
    }, [props?.paymentData]);

    const handlePrint = useReactToPrint({
      contentRef: printRef,
      onBeforeGetContent: () => {
        console.log("Before print - Invoice data:", invoiceData);
        if (!invoiceData || !printRef.current) {
          console.log("Print cancelled - missing data or ref");
          return Promise.reject("Missing data or ref");
        }
        return Promise.resolve();
      },
      onAfterPrint: () => {
        setInvoiceData(null);
      },
      onPrintError: (error) => {
        console.error("Print error:", error);
      },
    });

    useEffect(() => {
      if (invoiceData?.invoice_code) {
        console.log(
          "Preparing to print, invoice ID:",
          invoiceData.invoice_code,
        );
        setTimeout(() => {
          if (printRef.current) {
            handlePrint();
          } else {
            console.error("Print ref not available");
          }
        }, 1000);
      }
    }, [invoiceData, handlePrint]);

    const itemRenderer = (item, props) => {
      return (
        <div className="p-menuitem-content">
          {requiredButton?.includes("Add Payment") &&
            item.label === "Add Payment" &&
            Feature?.featureVerify(Feature.listOfFeature.paymentAdd) && (
              <Button
                className="card-button-add-button"
                icon="pi pi-plus"
                label="Add Payment"
                onClick={() => {
                  setMetaData({
                    invoice: props?.props?.itemProp,
                  });
                  setAddPaymentVisisble(true);
                }}
              />
            )}
          {requiredButton?.includes("Edit") &&
            item.label === "Edit" &&
            Feature?.featureVerify(Feature.listOfFeature.invoicesEdit) && (
              <Button
                className="card-button-edit"
                label="Edit"
                onClick={() => {
                  setMetaData({
                    invoice_edit: true,
                    invoice_id:
                      props?.props?.itemProp
                        ?.collectable_payment_description_id,
                  });
                  setAddInvoiceVisisble(true);
                }}
              />
            )}
          {requiredButton?.includes("delete") && item.label === "Remove" && (
            <Button
              className="card-button-delete"
              label="Delete"
              onClick={() => {}}
            />
          )}
          {requiredButton?.includes("invoice/print") &&
            item.label == "invoice/print" && (
              <Button
                className="card-button-edit"
                label="Invoice/print"
                onClick={() =>
                  fetchData(
                    props?.props?.itemProp?.collectable_payment_description_id,
                  )
                }
              />
            )}
        </div>
      );
    };

    const allowExpansion = (rowData) => {
      return Object.keys(rowData).length > 0;
    };

    useEffect(() => {
      init(navigator);
    }, [id]);
    const onRowExpansionTemplate = (data) => {
      return (
        <div className="md:p-3 sub-data-grid-table">
          <Payment
            header={true}
            paymentData={data?.payment_categories || []}
            headerAlias={{ name: "category", collectable_amount: "amount" }}
          />
        </div>
      );
    };

    const fetchAllInvoices = async () => {
      if (!navigator.totalRecords || navigator.totalRecords <= 0) {
        return [];
      }

      const exportObj = {
        query: {
          page: 1,
          limit: navigator.totalRecords,
          // sortFields: navigator.sortFields,
          searchCriteria: props?.patientId || navigator.searchCriteria || "",
        },
      };

      const res = await commonPostApi(
        API_ENDPOINTS.GET_ALL_COLLECTABLE_PAYMENTS,
        exportObj,
      );

      return res?.data?.data || [];
    };

    return (
      <div className="Patients__main-container">
        <div className="hidden">
          <div ref={printRef}>{<InvoicePrint invoiceData={invoiceData} />}</div>
        </div>
        <DataGrid
          header={
            header ? (
              header
            ) : (
              <DataGridHeader
                handleClick={handleClick}
                isSearchEnabled={props?.isSearchEnabled}
                isCalenderEnabled={false}
                isDownloadButton={true}
                exportCSV={async () => {
                  const allData = await fetchAllInvoices();
                  exportCSV(allData, columns, "Invoice_List");
                }}
                exportExcel={async () => {
                  const allData = await fetchAllInvoices();
                  exportExcel(allData, columns, "Invoice_List");
                }}
                exportPdf={async () => {
                  const allData = await fetchAllInvoices();
                  exportPdf(allData, columns, "Invoice_List");
                }}
                isButtonEnabledLeft={false}
                isButtonEnabledRight={Feature?.featureVerify(
                  Feature.listOfFeature.invoicesAdd,
                )}
                searchPlaceHolder={"Search Invoices"}
                currentPageHeader={"Invoice List"}
                onChange={handleSeachChange}
              />
            )
          }
          isPaginatorEnabled={props?.paymentData ? false : true}
          onSortClick={onSortClick}
          onRowCountChange={onRowCountChange}
          onPageChange={onPageChange}
          expandedRows={expandedRows}
          onRowToggle={onRowToggle}
          // onRowExpand={onRowExpand}
          // onRowCollapse={onRowCollapse}
          rowExpansionTemplate={onRowExpansionTemplate}
          allowExpansion={allowExpansion}
          isAccordionEnabled={accordion}
          sortable={true}
          navigator={navigator}
          column={columns}
          value={values}
          loading={loading}
          optionsView={
            props?.paymentData
              ? ""
              : (Feature?.featureVerify(Feature.listOfFeature.invoicesEdit) ||
                  Feature?.featureVerify(Feature.listOfFeature.paymentAdd)) && [
                  {
                    items: [
                      {
                        label: "Add Payment",
                        icon: "pi pi-pencil",
                        template: itemRenderer,
                      },
                      {
                        label: "Edit",
                        icon: "pi pi-upload",
                        template: itemRenderer,
                      },
                      {
                        label: "Remove",
                        icon: "pi pi-upload",
                        template: itemRenderer,
                      },
                      {
                        label: "invoice/print",
                        icon: "pi pi-upload",
                        template: itemRenderer,
                      },
                    ],
                  },
                ]
          }
        />
        {addInvoiceVisisble && (
          <AddInvoice
            edit={metaData?.invoice_edit}
            invoice_id={metaData?.invoice_id}
            visible={addInvoiceVisisble}
            patientId={props?.patientId}
            setVisible={(value) => {
              setMetaData({});
              setAddInvoiceVisisble(value);
            }}
            init={() => init(navigator)}
          />
        )}
        {addPaymentVisisble && (
          <AddPayments
            invoice={metaData?.invoice}
            visible={addPaymentVisisble}
            setVisible={(value) => {
              setMetaData({});
              setAddPaymentVisisble(value);
            }}
            init={() => init(navigator)}
          />
        )}
      </div>
    );
  },
);

export default Payment;
